'use client'
import * as React from 'react'
import { AlgoliaEventName, algoliaAnalytics } from 'algolia/algolia'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'
import { FacebookEventName, facebookEvent } from '@repo/analytics/facebook'
import { useSiteData } from '@repo/analytics/hooks/use-site-data'
import type { PublicContentFetchProductPageResponse } from 'cf-grpc/creativefabrica/content/v1/public_service_pb'
import { AssetType, AssetVariant } from 'cf-grpc/creativefabrica/content/v1/types_pb'
import { localStore } from 'utils/storage'

import type { CategoryPageType } from '../content-api/config'
import { mapCategoryPageTypeToCategoryDetails } from '../content-api/config'
import { NEXT_PUBLIC_CONTENT_IMAGES_ROOT_URL } from '@web/utils/env'

interface AnalyticsProps {
  productContent: PublicContentFetchProductPageResponse
}

export function Analytics({ productContent }: AnalyticsProps) {
  const siteData = useSiteData()
  const productData = productContent

  const rootCategory = productData?.category?.root
  const rootCategoryName = React.useMemo(() => {
    if (!rootCategory) {
      return
    }
    const rootCategoryType = Object.keys(mapCategoryPageTypeToCategoryDetails).find(item => {
      const categoryPageType = item as CategoryPageType
      return mapCategoryPageTypeToCategoryDetails[categoryPageType].parentId === rootCategory.id
    }) as CategoryPageType
    return mapCategoryPageTypeToCategoryDetails[rootCategoryType].categoryName
  }, [rootCategory])

  // oxlint-disable-next-line complexity
  React.useEffect(() => {
    if (!productData || !siteData.data) {
      return
    }

    const productId = productData.id
    const category = productData.category
    const categoryName = category?.name || ''
    const previewImage = productData?.assets
      .filter(asset => asset.isPublic && asset.type === AssetType.PREVIEW)
      .find(asset => asset.variant === AssetVariant.DESKTOP)
    const bannerAboveNav =
      siteData.data.allAccessBanner?.bannerAboveNav ||
      siteData.data.allAccessYearlyExtendBanner?.bannerAboveNav ||
      ''
    const bannerAboveNavState =
      siteData.data.allAccessBanner?.bannerAboveNavState ||
      siteData.data.allAccessYearlyExtendBanner?.bannerAboveNavState ||
      ''

    const productPosition = getAndRemoveLocalStorageProductPosition(productId)

    sendAmplitudeEvent(AmplitudeEvents.PRODUCT_VIEWED, {
      banner_above_nav: bannerAboveNav,
      banner_above_nav_state: bannerAboveNavState,
      category: rootCategoryName ?? 'Unknown',
      category_ID: rootCategory?.id ?? 'Unknown',
      subcategory: categoryName ? [categoryName] : [],
      image_url: `${NEXT_PUBLIC_CONTENT_IMAGES_ROOT_URL}/${previewImage?.filePath}`,
      name: productData.title || '',
      product_id: productId,
      productId: productId,
      view: 'main',
      product_position: productPosition,
    })

    facebookEvent(FacebookEventName.VIEW_CONTENT, {
      content_name: productData.title,
      content_ids: [productId],
      content_category: rootCategoryName ?? 'Unknown',
      content_type: 'product',
    })

    algoliaAnalytics('viewedObjectIDs', {
      index: process.env.NEXT_PUBLIC_CONTENT_ALGOLIA_INDEX ?? '',
      eventName: AlgoliaEventName.PRODUCT_VIEWED,
      objectIDs: [productId],
    })
  }, [productData, siteData.data, rootCategoryName, rootCategory])

  return null
}

export function saveLocalStorageProductPosition(productId: string, position: number) {
  localStore.setString(`productPosition.${productId}`, position.toString())
}

function getAndRemoveLocalStorageProductPosition(productId: string): number | null {
  const productPosition = localStore.getString(`productPosition.${productId}`)
  if (!productPosition) {
    return null
  }
  localStore.remove(`productPosition.${productId}`)
  // oxlint-disable-next-line radix
  return parseInt(productPosition)
}
